import { format } from "date-fns";

// Filter By is a set of independent filters, each switched on with a checkbox
// and all applied together (AND). Unchecking one removes it. Status starts on
// "pending" so the page opens as the pending queue, as it always has.
export const FILTER_FIELDS = [
  { key: "status", label: "Status" },
  { key: "request_date", label: "Request Date" },
  { key: "reviewed_by", label: "Reviewed By" },
  { key: "action", label: "Type" },
  { key: "cost_center", label: "Cost Center" },
  { key: "email", label: "Email" },
];

// The API only accepts reviewed_by together with one of these statuses (it returns 400 otherwise),
// so the Reviewed By filter can only be used while Status is one of them.
export const REVIEWER_STATUSES = ["approved", "rejected", "failed"];
export const canFilterByReviewer = (filters) => filters.status.on && REVIEWER_STATUSES.includes(filters.status.value);

export const STATUS_VALUE_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  // Approved, but applying it to GitHub then errored (e.g. a rate limit). Approve retries it.
  { value: "failed", label: "Failed" },
  { value: "all", label: "All" },
];

export const ACTION_VALUE_OPTIONS = [
  { value: "increase", label: "Increase" },
  { value: "decrease", label: "Decrease" },
  { value: "admin_set", label: "Admin Override" },
];

export const buildDefaultFilters = () => ({
  status: { on: true, value: "pending" },
  email: { on: false, value: "" },
  // A range: "from" alone filters that single day, "to" needs a "from".
  reviewed_by: { on: false, value: "" },
  request_date: { on: false, value: { from: null, to: null } },
  // Cost center ids; several can be picked.
  cost_center: { on: false, value: [] },
  action: { on: false, value: "" },
});

export const setFilterOn = (filters, key, on) => ({ ...filters, [key]: { ...filters[key], on } });

// Unticking drops the value too, so re-ticking starts clean.
export const toggleFilter = (filters, key) =>
  filters[key].on
    ? { ...filters, [key]: { on: false, value: buildDefaultFilters()[key].value } }
    : setFilterOn(filters, key, true);

export const setFilterValue = (filters, key, value) => ({ ...filters, [key]: { ...filters[key], value } });

export const activeFilterKeys = (filters) => FILTER_FIELDS.map((f) => f.key).filter((key) => filters[key].on);

// A Date (from the picker) or a YYYY-MM-DD string -> the YYYY-MM-DD the API wants.
const toApiDate = (value) => {
  if (value instanceof Date && !isNaN(value)) return format(value, "yyyy-MM-dd");
  return typeof value === "string" ? value : "";
};

// Query params for GET budget requests. A filter that is off (or on but not
// filled in yet) sends nothing / "all". `email` and the reviewer are passed in already debounced.
export const buildFilterParams = (filters, debouncedEmail = filters.email.value, debouncedReviewer = filters.reviewed_by.value) => {
  const requestDate = filters.request_date.on ? toApiDate(filters.request_date.value?.from) : "";
  return {
    status: filters.status.on && filters.status.value ? filters.status.value : "all",
    action: filters.action.on && filters.action.value ? filters.action.value : "all",
    email: filters.email.on ? debouncedEmail || "" : "",
    requestDate,
    // The API only accepts an end date alongside a start date.
    requestDateTo: requestDate ? toApiDate(filters.request_date.value?.to) : "",
    // Comma-separated ids, as the API expects for several cost centers.
    costCenterId: filters.cost_center.on ? filters.cost_center.value.join(",") : "",
    // Only valid alongside an approved / rejected / failed status; otherwise leave it out.
    reviewedBy: canFilterByReviewer(filters) && filters.reviewed_by.on ? (debouncedReviewer || "").trim() : "",
  };
};

// Everything off — shows every request.
export const clearAllFilters = (filters) =>
  FILTER_FIELDS.reduce((next, { key }) => ({ ...next, [key]: { on: false, value: buildDefaultFilters()[key].value } }), filters);

const shortDate = (value) => (value instanceof Date && !isNaN(value) ? format(value, "MM/dd/yyyy") : "");

// One removable chip per filter that is on and filled in — what the user sees
// under the header so they know what is applied. A ticked filter with nothing
// chosen yet filters nothing, so it gets no chip.
export const getFilterChips = (filters, { costCenterNameById = {} } = {}) => {
  const chips = [];
  const add = (key, label, text) => text && chips.push({ key, label, text });
  if (filters.status.on) add("status", "Status", STATUS_VALUE_OPTIONS.find((o) => o.value === filters.status.value)?.label);
  if (filters.request_date.on) {
    const from = shortDate(filters.request_date.value?.from);
    const to = shortDate(filters.request_date.value?.to);
    add("request_date", "Date", from && to && to !== from ? `${from} – ${to}` : from);
  }
  if (filters.action.on) add("action", "Type", ACTION_VALUE_OPTIONS.find((o) => o.value === filters.action.value)?.label);
  if (filters.cost_center.on && filters.cost_center.value.length) {
    const names = filters.cost_center.value.map((id) => costCenterNameById[id] || id);
    add("cost_center", "Cost Center", names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]);
  }
  if (filters.email.on) add("email", "Email", filters.email.value.trim());
  if (filters.reviewed_by.on && canFilterByReviewer(filters)) add("reviewed_by", "Reviewed By", filters.reviewed_by.value.trim());
  return chips;
};
